import type {
  GameWeather,
  HourlySlice,
  RainoutRisk,
  RoofClass,
  WeatherFlags,
  WindProfile,
  WindRelativeToCf,
} from './types';
import { resolveGameRoofState } from './semantics';

const OUT_WIND_MPH = 15;
const IN_WIND_MPH = 15;
const CROSS_WIND_MPH = 20;
const COLD_F = 50;
const HEAT_F = 95;

function normalize360(deg: number): number {
  const m = deg % 360;
  return m < 0 ? m + 360 : m;
}

function angleDiff(a: number, b: number): number {
  const d = Math.abs(normalize360(a) - normalize360(b));
  return d > 180 ? 360 - d : d;
}

/**
 * Classify wind against a stadium's center-field bearing.
 * `windFromDeg` is the meteorological direction the wind is blowing FROM (Open-Meteo convention).
 * A wind blowing FROM south (180°) in a stadium where CF points N (0°) is a wind "out to CF".
 * So wind vector direction (where it's going TO) = windFromDeg + 180 (mod 360).
 */
export function classifyWind(
  windFromDeg: number,
  cfBearingDeg: number,
  speedMph: number,
): WindRelativeToCf {
  if (speedMph < 4) return 'calm';
  const windToDeg = normalize360(windFromDeg + 180);
  const toCf = angleDiff(windToDeg, cfBearingDeg);
  const fromCf = angleDiff(windFromDeg, cfBearingDeg);
  if (toCf <= 45) return 'out';
  if (fromCf <= 45) return 'in';
  // Cross-wind: determine L-R vs R-L relative to home→CF axis.
  // Signed angle from CF bearing to wind-to direction, range (-180, 180].
  const signed = ((windToDeg - cfBearingDeg + 540) % 360) - 180;
  return signed > 0 ? 'cross-lr' : 'cross-rl';
}

export function classifyRainout(probPct: number, amountIn: number): RainoutRisk {
  // Combine probability with accumulated precip. A 40% chance of 0.5" is worse than 40% of 0.02".
  if (probPct >= 75 || amountIn >= 0.4) return 'high';
  if (probPct >= 50 || amountIn >= 0.2) return 'medium';
  if (probPct >= 20 || amountIn >= 0.05) return 'low';
  return 'none';
}

export function classifyWindProfile(
  relativeToCf: WindRelativeToCf,
  speedMph: number,
): WindProfile {
  if (relativeToCf === 'calm') return 'calm';
  if (relativeToCf === 'out' && speedMph >= OUT_WIND_MPH) return 'out';
  if (relativeToCf === 'in' && speedMph >= IN_WIND_MPH) return 'in';
  if ((relativeToCf === 'cross-lr' || relativeToCf === 'cross-rl') && speedMph >= CROSS_WIND_MPH) {
    return 'crosswind';
  }
  return 'calm';
}

export function computeFlags(input: {
  precipPct: number;
  precipAmountIn: number;
  windProfile: WindProfile;
  feelsLikeF: number;
  indoorVenue: boolean;
}): WeatherFlags {
  if (input.indoorVenue) {
    return {
      rainoutRisk: 'none',
      windProfile: 'calm',
      extremeCold: false,
      extremeHeat: false,
    };
  }
  return {
    rainoutRisk: classifyRainout(input.precipPct, input.precipAmountIn),
    windProfile: input.windProfile,
    extremeCold: input.feelsLikeF < COLD_F,
    extremeHeat: input.feelsLikeF > HEAT_F,
  };
}

/**
 * Single-label severity pick: the one badge to show on a compact pill.
 * Order: rainout > extreme wind > extreme cold/heat > calm/none.
 * Humidity gets no compact badge — it falls through to the plain temperature.
 */
export type WeatherSeverityLabel =
  | { kind: 'indoor' }
  | { kind: 'rain'; risk: RainoutRisk; probPct: number }
  | { kind: 'wind-out'; mph: number }
  | { kind: 'wind-in'; mph: number }
  | { kind: 'wind-cross'; mph: number }
  | { kind: 'cold'; tempF: number; feelsLikeF: number }
  | { kind: 'heat'; tempF: number; feelsLikeF: number }
  | { kind: 'clear'; tempF: number };

export function pickSeverity(w: GameWeather): WeatherSeverityLabel {
  if (w.indoorVenue) return { kind: 'indoor' };
  const f = w.flags;
  if (f.rainoutRisk === 'high' || f.rainoutRisk === 'medium') {
    return { kind: 'rain', risk: f.rainoutRisk, probPct: w.precip.probabilityPct };
  }
  if (f.windProfile === 'out') return { kind: 'wind-out', mph: w.wind.speedMph };
  if (f.windProfile === 'in') return { kind: 'wind-in', mph: w.wind.speedMph };
  if (f.windProfile === 'crosswind') return { kind: 'wind-cross', mph: w.wind.speedMph };
  if (f.rainoutRisk === 'low') return { kind: 'rain', risk: 'low', probPct: w.precip.probabilityPct };
  if (f.extremeCold) return { kind: 'cold', tempF: w.tempF, feelsLikeF: w.feelsLikeF };
  if (f.extremeHeat) return { kind: 'heat', tempF: w.tempF, feelsLikeF: w.feelsLikeF };
  // High humidity intentionally does NOT get its own badge — show the temperature.
  return { kind: 'clear', tempF: w.tempF };
}

/**
 * Assemble GameWeather from hourly slices + venue data.
 * Exported so the route handler and tests share the exact same composition path.
 */
export function buildGameWeather(params: {
  firstPitchIso: string;
  hourly: HourlySlice[];
  feelsLikeHourly: number[]; // same length as hourly (first element aligned to firstPitchIso)
  humidityHourly: number[];
  cloudHourly: number[];
  gustsHourly: number[];
  precipAmountHourly: number[]; // inches per hour
  pressureHourly: number[]; // hPa, station level
  cfBearingDeg: number;
  roof: RoofClass;
  officialCondition?: string | null;
  venueTimezone: string;
}): GameWeather {
  const { hourly, feelsLikeHourly, humidityHourly, cloudHourly, gustsHourly, precipAmountHourly, pressureHourly, cfBearingDeg, roof, venueTimezone } = params;
  const roofState = resolveGameRoofState(roof, params.officialCondition);
  const indoorVenue = roofState.state === 'closed';

  const head = hourly[0] ?? { iso: params.firstPitchIso, tempF: 70, precipPct: 0, windMph: 0, windDirDeg: 0 };
  const window = hourly.slice(0, 4);
  const maxPrecipPct = window.length ? Math.max(...window.map(h => h.precipPct)) : 0;
  const sumPrecipAmount = precipAmountHourly.slice(0, window.length).reduce((s, v) => s + (v ?? 0), 0);

  const relativeToCf = classifyWind(head.windDirDeg, cfBearingDeg, head.windMph);
  const windProfile = classifyWindProfile(relativeToCf, head.windMph);

  const feelsLikeF = feelsLikeHourly[0] ?? head.tempF;
  const humidity = humidityHourly[0] ?? 50;
  const cloudCover = cloudHourly[0] ?? 0;
  const gustsMph = gustsHourly[0] ?? head.windMph;

  // Mean over the hours a game actually occupies — mirrors EXPOSURE_HOURS in
  // build_air_density.py. Falls back to the first-pitch value, then to the
  // standard atmosphere, so a short forecast tail degrades rather than throws.
  const meanOverWindow = (
    series: number[],
    fallback: number,
  ): { value: number; present: boolean } => {
    const slice = series.slice(0, Math.max(window.length, 1)).filter(v => Number.isFinite(v));
    if (!slice.length) return { value: fallback, present: false };
    return { value: slice.reduce((sum, v) => sum + v, 0) / slice.length, present: true };
  };
  const humidityWindow = meanOverWindow(humidityHourly, humidity);
  const pressureWindow = meanOverWindow(pressureHourly, 1013.25);

  return {
    tempF: head.tempF,
    feelsLikeF,
    humidity,
    cloudCover,
    airDensity: {
      humidityPctMean4h: humidityWindow.value,
      pressureHpaMean4h: pressureWindow.value,
      // Both terms share one flag because the model carries one family flag.
      // Either series missing makes the pair untrustworthy.
      present: humidityWindow.present && pressureWindow.present,
    },
    wind: {
      speedMph: head.windMph,
      gustsMph,
      directionDeg: head.windDirDeg,
      relativeToCf,
    },
    precip: {
      probabilityPct: maxPrecipPct,
      amountIn: sumPrecipAmount,
    },
    hourly: window,
    flags: computeFlags({
      precipPct: maxPrecipPct,
      precipAmountIn: sumPrecipAmount,
      windProfile,
      feelsLikeF,
      indoorVenue,
    }),
    indoorVenue,
    roof,
    roofState: roofState.state,
    roofStateSource: roofState.source,
    cfBearingDeg,
    firstPitchIso: params.firstPitchIso,
    venueTimezone,
    source: 'open-meteo',
  };
}
